import { useCallback, useEffect, useState } from "react";

interface UseScrollAtBottomOptions {
  threshold?: number;
  enabled?: boolean;
}

interface UseScrollAtBottomReturn {
  isAtBottom: boolean;
  canScroll: boolean;
  checkScroll: () => void;
  isAtTop: boolean;
}

export function useScrollAtBottom(
  el: HTMLElement | null,
  options: UseScrollAtBottomOptions = {},
): UseScrollAtBottomReturn {
  const { threshold = 10, enabled = true } = options;

  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isAtTop, setIsAtTop] = useState(true);
  const [canScroll, setCanScroll] = useState(false);

  const checkScroll = useCallback(() => {
    if (!el) return;

    const { scrollHeight, scrollTop, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    setIsAtBottom(distanceFromBottom < threshold);
    setIsAtTop(scrollTop < threshold);
    setCanScroll(scrollHeight > clientHeight);
  }, [el, threshold]);

  useEffect(() => {
    if (!el || !enabled) return;

    checkScroll();

    el.addEventListener("scroll", checkScroll, { passive: true });

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      resizeObserver.disconnect();
    };
  }, [el, enabled, checkScroll]);

  return { isAtBottom, canScroll, checkScroll, isAtTop };
}
